import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import Drawer from '@material-ui/core/Drawer';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import LinearProgress from '@material-ui/core/LinearProgress';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CloseIcon from '@material-ui/icons/Close';
import moment from 'moment';
import api from '../../services/api';
import { pode } from '../../services/acesso';
import { CORES } from '../../theme';
import { situacaoFollowUp } from '../../utils/followUp';
import StatusChip from '../../components/StatusChip';
import ContatoRapido from '../../components/ContatoRapido';

const TOM = {
    erro: { background: '#FBE6DF', color: '#8E2F12' },
    alerta: { background: '#FFF3DC', color: '#7A4A05' },
    ok: { background: '#E4EFDF', color: '#1F4615' },
};

const formatarCnpj = (valor) => {
    const d = String(valor || '').replace(/\D/g, '');
    if (d.length !== 14) return valor || '';
    return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

const ultimoContato = (relatorios) => {
    if (!Array.isArray(relatorios) || relatorios.length === 0) return null;
    const comData = relatorios.filter((r) => r && r.data);
    if (comData.length === 0) return relatorios[0];
    return comData.slice().sort((a, b) => moment(b.data).valueOf() - moment(a.data).valueOf())[0];
};

const Linha = ({ rotulo, children }) => (
    <>
        <dt style={{ color: CORES.textoSuave }}>{rotulo}</dt>
        <dd style={{ margin: 0, minWidth: 0, overflowWrap: 'anywhere' }}>{children || '—'}</dd>
    </>
);

/**
 * Painel lateral do board: resumo do lead e ações rápidas, sem sair da tela
 * (antes cada lead abria em uma nova aba e o board não refletia o que foi salvo).
 * Usa o endpoint que já existe: GET lead/{id}?mailing_leads_id=...
 */
const LeadDrawer = ({ card, colunaId, colunas = [], assessores, assessorAtual, onFechar, onTrocarAssessor, onMover }) => {
    const history = useHistory();
    const [lead, setLead] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const aberto = Boolean(card);

    useEffect(() => {
        if (!card) return undefined;
        let ativo = true;
        setLead(null);
        setCarregando(true);
        api.get(`lead/${card.lead_id}`, { params: { mailing_leads_id: card.mailing_leads_id || null } })
            .then(({ data }) => { if (ativo) setLead(data); })
            .catch(() => { if (ativo) setLead(null); })
            .finally(() => { if (ativo) setCarregando(false); });
        return () => { ativo = false; };
        // recarrega só quando muda o lead (trocar o assessor não precisa buscar de novo)
    }, [card && card.lead_id, card && card.mailing_leads_id]);

    if (!card) return <Drawer anchor="right" open={false} onClose={onFechar} />;

    const follow = situacaoFollowUp(card.follow_up);
    const decisor = lead && Array.isArray(lead.responsible) && lead.responsible.length > 0 ? lead.responsible[0] : null;
    const contato = lead ? ultimoContato(lead.relatorios) : null;
    const linkCompleto = `/leads/${card.lead_id}/edit?mailing_leads_id=${card.mailing_leads_id}`;

    return (
        <Drawer anchor="right" open={aberto} onClose={onFechar} PaperProps={{ style: { width: 380, maxWidth: '100vw' } }}>
            <aside aria-label="Resumo do lead" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14, height: '100%', boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 18, fontWeight: 700 }}>{card.title}</div>
                        {lead && lead.cnpj && <div style={{ fontSize: 13, color: CORES.textoSuave }}>{formatarCnpj(lead.cnpj)}</div>}
                    </div>
                    <IconButton size="small" onClick={onFechar} aria-label="Fechar painel">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {colunaId !== undefined && !(pode('editar_leads') && onMover) && <StatusChip tipo="lead" valor={colunaId} />}
                    {colunaId !== undefined && pode('editar_leads') && onMover && (
                        <TextField
                            select
                            size="small"
                            variant="outlined"
                            label="Situação"
                            value={colunaId}
                            onChange={(e) => onMover(card, e.target.value)}
                            style={{ minWidth: 200 }}
                        >
                            {colunas.map((c) => <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>)}
                        </TextField>
                    )}
                    {follow && (
                        <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 6, ...TOM[follow.tom] }}>
                            Follow-up: {follow.texto}{follow.tom !== 'ok' ? ` (${follow.data.format('DD/MM')})` : ''}
                        </span>
                    )}
                </div>

                {carregando && <LinearProgress />}

                <dl style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 10px', fontSize: 14, margin: 0 }}>
                    <Linha rotulo="Assessor">
                        {pode('editar_leads') ? (
                            <Autocomplete
                                options={assessores}
                                value={assessorAtual || null}
                                getOptionLabel={(o) => (o && o.name) || ''}
                                getOptionSelected={(o, v) => o && v && o.id === v.id}
                                onChange={(e, novo) => onTrocarAssessor(card, novo)}
                                renderInput={(params) => <TextField {...params} size="small" placeholder="Sem assessor" />}
                            />
                        ) : (assessorAtual && assessorAtual.name)}
                    </Linha>
                    <Linha rotulo="Mailing">{card.content}</Linha>
                    <Linha rotulo="Decisor">{decisor ? [decisor.name, decisor.cargo].filter(Boolean).join(' · ') : null}</Linha>
                    <Linha rotulo="Contato">
                        {(decisor && (decisor.telephone || decisor.email || decisor.linkedin)) || (lead && lead.telephone) ? (
                            <ContatoRapido
                                telefone={(decisor && decisor.telephone) || (lead && lead.telephone)}
                                email={decisor && decisor.email}
                                linkedin={decisor && decisor.linkedin}
                            />
                        ) : null}
                    </Linha>
                    <Linha rotulo="Último contato">
                        {contato ? (
                            <>
                                {contato.data && <b>{moment(contato.data).format('DD/MM/YYYY')} — </b>}
                                {contato.resumo}
                            </>
                        ) : null}
                    </Linha>
                </dl>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 'auto' }}>
                    {pode('editar_reunioes') && (
                        <Button variant="contained" color="primary" onClick={() => history.push(`/agendamento-lead/novo?id_lead=${card.lead_id}`)}>
                            Agendar reunião
                        </Button>
                    )}
                    <Button variant="outlined" component={Link} to={linkCompleto}>
                        {pode('editar_leads') ? 'Registrar contato / editar cadastro' : 'Abrir cadastro completo'}
                    </Button>
                </div>
            </aside>
        </Drawer>
    );
};

export default LeadDrawer;
